import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import engText from '../language/Eng_eng.json';

const HomeContainer = styled.div`
  font-family: 'Inter', system-ui, sans-serif;
  color: #1a1a1a;
  background-color: #ffffff;
  padding-top: 80px; /* Offset for the fixed headers */
  overflow-x: hidden;

  @media (max-width: 768px) {
    padding-top: 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: 40px;
  font-weight: 300;
  text-transform: uppercase;
  letter-spacing: 4px;
  margin-bottom: 60px;
  text-align: center;
  color: #1a1a1a;
`;

const Section = styled.section`
  padding: 100px 8%;
  scroll-margin-top: 85px;
`;

const Btn = styled.a`
  display: inline-block;
  padding: 15px 35px;
  background-color: transparent;
  color: #1a1a1a;
  border: 1px solid #1a1a1a;
  text-decoration: none;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 2px;
  text-transform: uppercase;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    background-color: #1a1a1a;
    color: #ffffff;
  }
`;

// Hero Section
const HeroSectionWrapper = styled(Section)`
  min-height: 85vh;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6vw;
  background-color: #ffffff;
  padding: 50px 8%;

  @media (max-width: 900px) {
    flex-direction: column;
    gap: 40px;
    padding-top: 40px;
  }
`;

const HeroImage = styled.div`
  flex: 1;
  position: relative;
  height: 600px;
  min-height: 600px;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);

  @media (max-width: 900px) {
    flex: none;
    width: 100%;
    height: 400px;
    min-height: 400px;
  }

  @media (max-width: 520px) {
    height: 320px;
    min-height: 320px;
  }

  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0;
    transition: opacity 1s ease-in-out;

    &.active {
      opacity: 1;
    }
  }
`;

const HeroContent = styled.div`
  flex: 1;
  text-align: left;

  p {
    font-size: 16px;
    line-height: 1.9;
    margin-bottom: 25px;
    color: #555555;
    font-weight: 300;
  }

  ${Btn} {
    margin-top: 10px;
  }
`;

// Drink & Eat Section
const DrinkEatSectionWrapper = styled(Section)`
  background-color: #ffffff;
`;

const BrandsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 40px;
  max-width: 1400px;
  margin: 0 auto;
`;

const BrandItem = styled.div`
  display: flex;
  flex-direction: column;
  width: 420px;
  flex: 1 1 380px;
  max-width: 420px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  background-color: #ffffff;
  border: 1px solid #eeeeee;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.08); /* Lift shadow on hover */
  }
`;

const BrandImage = styled.div<{ $contain?: boolean }>`
  position: relative;
  overflow: hidden;
  width: 100%;
  background-color: ${props => props.$contain ? '#ffffff' : 'transparent'};

  img {
    width: 100%;
    height: 250px;
    object-fit: ${props => props.$contain ? 'contain' : 'cover'};
    padding: ${props => props.$contain ? '20px' : '0'};
    box-sizing: border-box;
    display: block;
  }
`;

const BrandInfo = styled.div`
  padding: 30px;
  display: flex;
  flex-direction: column;
  flex-grow: 1;

  h3 {
    font-size: 22px;
    font-weight: 500;
    margin-top: 0;
    margin-bottom: 15px;
    letter-spacing: 1.5px;
    color: #1a1a1a;
  }

  p {
    font-size: 14px;
    line-height: 1.6;
    color: #666666;
    margin-bottom: 25px;
    font-weight: 300;
    flex-grow: 1;
    white-space: pre-line;
  }

  ${Btn} {
    align-self: flex-start;
    padding: 10px 25px;
    font-size: 12px;
  }
`;

// Events Section
const EventsSectionWrapper = styled(Section)`
  background-color: #f9f9f9;
  text-align: center;
`;

const EventsIntro = styled.p`
  font-size: 18px;
  margin-bottom: 60px;
  color: #555555;
  font-weight: 300;
`;

const EventsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  max-width: 1100px;
  margin: 0 auto;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const EventCard = styled.div<{ bgImage: string }>`
  position: relative;
  overflow: hidden;
  border-radius: 4px;
  height: 450px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  padding: 50px 30px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  background-size: cover;
  background-position: center;
  background-image: url(${props => props.bgImage});

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: linear-gradient(to top, rgba(0,0,0,0.8), rgba(0,0,0,0.1));
    z-index: 1;
  }

  h3 {
    position: relative;
    z-index: 2;
    font-size: 30px;
    margin-bottom: 25px;
    letter-spacing: 2px;
    font-weight: 400;
    color: #ffffff;
  }

  ${Btn} {
    position: relative;
    z-index: 2;
    color: #ffffff;
    border-color: #ffffff;
    &:hover {
      background-color: #ffffff;
      color: #1a1a1a;
    }
  }
`;

// Contact Section
const ContactSectionWrapper = styled(Section)`
  display: flex;
  background-color: #ffffff;
  gap: 6vw;
  align-items: flex-start;
  border-top: 1px solid #eeeeee;

  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

const ContactInfo = styled.div`
  flex: 1;

  h2 {
    font-size: 36px;
    margin-top: 0;
    margin-bottom: 40px;
    font-weight: 300;
    letter-spacing: 2px;
    color: #1a1a1a;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0 0 40px 0;
  }

  li {
    font-size: 16px;
    margin-bottom: 20px;
    color: #555555;
    display: flex;
  }

  strong {
    color: #1a1a1a;
    margin-right: 15px;
    width: 30px;
  }
`;

const ContactFormContainer = styled.div`
  flex: 1;
  background-color: #fcfcfc;
  padding: 50px;
  border-radius: 4px;
  border: 1px solid #eeeeee;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.02);

  @media (max-width: 900px) {
    width: 100%;
    box-sizing: border-box;
    padding: 30px 20px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 25px;

  input, textarea, select {
    width: 100%;
    padding: 18px;
    background-color: #ffffff;
    border: 1px solid #dddddd;
    color: #1a1a1a;
    font-family: inherit;
    font-size: 15px;
    box-sizing: border-box;
    transition: border-color 0.3s ease;
    border-radius: 2px;

    &:focus {
      outline: none;
      border-color: #1a1a1a;
    }
  }

  textarea {
    resize: vertical;
    min-height: 120px;
  }
`;

const SubmitBtn = styled.button`
  width: 100%;
  padding: 18px;
  background-color: #1a1a1a;
  color: #ffffff;
  border: none;
  font-weight: 600;
  font-size: 15px;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;

  &:hover {
    background-color: #c9a779;
    color: #ffffff;
    border-color: #c9a779;
  }
`;

const FooterBottom = styled.footer`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  background-color: #f1f1f1;
  color: #777777;
  font-size: 13px;
  letter-spacing: 1px;

  p {
    margin: 0;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
    padding: 20px;
  }
`;

const SocialLinks = styled.div`
  position: absolute;
  right: 6%;
  display: flex;
  gap: 15px;

  @media (max-width: 768px) {
    position: static;
  }

  a {
    color: #777777;
    transition: color 0.3s ease;
    display: flex;
    align-items: center;

    &:hover {
      color: #1a1a1a;
    }

    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const heroImages = [
  "/logo/allta.png",
  "/logo/buttered.png",
  "/logo/firestone.png",
  "/logo/willandco.png",
  "/logo/hongdae_logo1.webp",
  "/logo/joie_logo.webp"
];

const Home: React.FC = () => {
  const { hero, drink_eat, events_venues, contact, top_menu } = engText;

  const handleContactSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const name = formData.get('name') as string;
    const phone = formData.get('phone') as string;
    const emailForm = formData.get('email') as string;
    const enquiry = formData.get('enquiry') as string;
    const message = formData.get('message') as string;

    const subject = encodeURIComponent(`[Vuza Group] Enquiry from ${name}`);
    const body = encodeURIComponent(`Name: ${name}\nPhone: ${phone}\nEmail: ${emailForm}\nEnquiry Type: ${enquiry}\n\nMessage:\n${message}`);

    const targetEmail = contact.email;
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    if (isMobile) {
      // Use native mail app on mobile devices
      window.location.href = `mailto:${targetEmail}?subject=${subject}&body=${body}`;
    } else {
      // Force Gmail web interface on PC
      const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${targetEmail}&su=${subject}&body=${body}`;
      window.open(gmailUrl, '_blank');
    }
  };

  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % heroImages.length);
    }, 3000);

    return () => clearInterval(timer);
  }, [heroImages.length]);

  const brands = [
    {
      id: 'allta',
      data: drink_eat.allta,
      image: "/logo/allta.png",
      url: "https://alltasydney.com.au/"
    },
    {
      id: 'firestone',
      data: drink_eat.firestone,
      image: "/logo/firestone.png",
      url: "https://firestone-sydney.com.au/"
    },
    {
      id: 'will_co',
      data: drink_eat.will_co,
      image: "/logo/willandco.png",
      url: "https://www.instagram.com/willandcochatswood?igsh=MXhwdG5zd3VqMmRuZA%3D%3D&utm_source=qr",
      btnText: "VISIT SNS"
    },
    {
      id: 'buttered',
      data: drink_eat.buttered,
      image: "/logo/buttered.png",
      url: "https://butteredsydney.com.au/"
    },
    {
      id: 'hongdae',
      data: drink_eat.hongdae_pocha,
      image: "/logo/hongdae_logo1.webp",
      url: "https://hongdaepocha.com.au/"
    },
    {
      id: 'joie',
      data: drink_eat.joie,
      image: "/logo/joie_logo.webp",
      url: "https://joiesydney.com.au/"
    }
  ];

  return (
    <HomeContainer>
      {/* Hero Section */}
      <HeroSectionWrapper id="home">
        <HeroImage>
          {heroImages.map((src, index) => (
            <img
              key={index}
              src={src}
              alt={`Vuza Group ${index}`}
              className={index === currentImageIndex ? 'active' : ''}
            />
          ))}
        </HeroImage>
        <HeroContent>
          <p>{hero.desc_1}</p>
          <p>{hero.desc_2}</p>
          <p>{hero.desc_3}</p>
          <p>{hero.desc_4}</p>
        </HeroContent>
      </HeroSectionWrapper>

      {/* Drink & Eat Section */}
      <DrinkEatSectionWrapper id="drink-eat">
        <SectionTitle>{top_menu.drink_eat}</SectionTitle>
        <BrandsGrid>
          {brands.map((brand) => (
            <BrandItem key={brand.id} id={brand.id}>
              <BrandImage $contain={brand.id === 'buttered' || brand.id === 'hongdae'}>
                <img src={brand.image} alt={brand.data.title} />
              </BrandImage>
              <BrandInfo>
                <h3>{brand.data.title}</h3>
                <p>{brand.data.desc}</p>
                <Btn href={brand.url} target="_blank" rel="noopener noreferrer">
                  {brand.btnText || brand.data.button}
                </Btn>
              </BrandInfo>
            </BrandItem>
          ))}
        </BrandsGrid>
      </DrinkEatSectionWrapper>

      {/* Events & Venues Section */}
      <EventsSectionWrapper id="events-venues">
        <SectionTitle>{top_menu.events_venues}</SectionTitle>
        <EventsIntro>{events_venues.desc_1}</EventsIntro>
        <EventsGrid>
          <EventCard bgImage="/dummy_image/Gemini_Generated_Image_gqvwywgqvwywgqvw.png">
            <h3>{events_venues.corporate}</h3>
            <Btn href="#contact">{events_venues.contact_button}</Btn>
          </EventCard>
          <EventCard bgImage="/dummy_image/Gemini_Generated_Image_3gln5r3gln5r3gln.png">
            <h3>{events_venues.celebrations}</h3>
            <Btn href="#contact">{events_venues.contact_button}</Btn>
          </EventCard>
        </EventsGrid>
      </EventsSectionWrapper>

      {/* Contact Section */}
      <ContactSectionWrapper id="contact">
        <ContactInfo>
          <h2>{contact.form.title}</h2>
          <ul>
            <li><strong>{contact.address_prefix.trim()}</strong> {contact.address}</li>
            <li><strong>{contact.phone_prefix.trim()}</strong> {contact.phone}</li>
            <li><strong>{contact.email_prefix.trim()}</strong> {contact.email}</li>
          </ul>
        </ContactInfo>
        <ContactFormContainer>
          <form onSubmit={handleContactSubmit}>
            <FormGroup>
              <input type="text" name="name" placeholder={contact.form.name} required />
            </FormGroup>
            <FormGroup>
              <input type="tel" name="phone" placeholder={contact.form.phone} required />
            </FormGroup>
            <FormGroup>
              <input type="email" name="email" placeholder={contact.form.email} required />
            </FormGroup>
            <FormGroup>
              <select name="enquiry" required defaultValue="">
                <option value="" disabled>{contact.form.enquiry}</option>
                <option value="Corporate Event">Corporate Event</option>
                <option value="Celebration">Celebration</option>
                <option value="General Enquiry">General Enquiry</option>
              </select>
            </FormGroup>
            <FormGroup>
              <textarea name="message" placeholder={contact.form.message} required></textarea>
            </FormGroup>
            <SubmitBtn type="submit">{contact.form.submit}</SubmitBtn>
          </form>
        </ContactFormContainer>
      </ContactSectionWrapper>

      {/* Footer */}
      <FooterBottom>
        <p>{contact.copyright}</p>
        <SocialLinks>
          <a href="https://www.instagram.com/vuzahospitalitygroup?utm_source=qr" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
              <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
              <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
            </svg>
          </a>
          <a href="https://www.facebook.com/profile.php?id=61552733460446" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3.81l.39-4H14V7a1 1 0 0 1 1-1h3z"></path>
            </svg>
          </a>
        </SocialLinks>
      </FooterBottom>
    </HomeContainer>
  );
};

export default Home;
